// Story 15.2 — the Screen 24 seat-capacity write (AC3, AC9).
//
// Action-in-path (Rule 06): `POST /admin/orgs/{id}/adjust-seats`. The endpoint
// shipped with Story 13.4 and this hook is its FIRST product caller — no backend
// change accompanies it.
//
// `seat_capacity` is an ABSOLUTE value, not a delta
// (`adjust_seats/schemas.py:16`). A delta computed here would re-derive a base
// the client may be holding stale.
//
// INVALIDATE-AND-REFETCH, BOTH KEYS — never `setQueryData`. The 200 body is the
// seat pool alone (four fields), while the detail cache holds the whole FR74
// record, so patching it in would be the shape `useAdminUserActions.ts:1-8`
// rejects in writing. The directory list is invalidated by PREFIX because a
// capacity change alters a directory ROW's seat column in every cached filter
// permutation, not only the one last viewed.
//
// There is NO server-side cache to bust: `adjust_seats/command.py:22-24` records
// that `OrgSeatProvider.resolve_access` never reads `seat_capacity`. Everything
// refreshed here is TanStack cache on the operator's own screen.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ADMIN_CONSOLE_QUERY_KEY } from "@/hooks/queries/useAdminConsole";
import { ADMIN_ORG_TOTALS_QUERY_KEY } from "@/hooks/queries/useAdminOrgTotals";
import { ADMIN_ORGS_QUERY_KEY } from "@/hooks/queries/useAdminOrgs";
import { adminOrgDetailQueryKey } from "@/hooks/queries/useAdminOrgDetail";
import { apiFetch } from "@/lib/api-client";

/** Mirrors `adjust_seats/schemas.py :: AdjustSeatsRequest` on the wire. */
export interface AdjustSeatsRequest {
  seat_capacity: number;
}

/** Mirrors `adjust_seats/schemas.py :: AdjustSeatsData`. `remaining` is derived server-side. */
export interface AdjustSeatsData {
  organization_id: string;
  seat_capacity: number;
  seat_consumed: number;
  remaining: number;
}

export function adjustSeatsPath(organizationId: string): string {
  return `/admin/orgs/${encodeURIComponent(organizationId)}/adjust-seats`;
}

export function useAdjustSeats(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: AdjustSeatsRequest) =>
      apiFetch<AdjustSeatsData>(adjustSeatsPath(organizationId), {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOrgDetailQueryKey(organizationId) });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORGS_QUERY_KEY] });
      // TWO KEYS, and since DW-423 the three tiles seat capacity feeds are
      // split across them: "Orgs at seat limit" is a console KPI, while
      // "Purchased seats" and "Assigned seats" moved to Screen 23's own totals
      // endpoint. Dropping either line leaves one screen showing a stale number
      // with nothing on it to say so.
      void queryClient.invalidateQueries({ queryKey: [ADMIN_CONSOLE_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORG_TOTALS_QUERY_KEY] });
    },
  });
}
